
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { IconSearch, IconFilter, IconRefresh, IconChevronUp, IconChevronDown, IconCirclePlus, IconPencil, IconTrash, IconX } from "@tabler/icons-react";
import { Link, useLocation } from "react-router-dom";
// Redux
import { useDispatch, useSelector } from "react-redux";
import {
  fetchTemplates,
  fetchCategoriesAndSubcategories,
  searchAndFilterFromAllTemplate,
  updateFilters,
  resetFilters,
  deleteTemplates,
  clearDeleteError
} from "../../redux/slices/templatesSlice";
import { useAdminLayout } from "../../context/adminLayoutUseContext";
import LoadingFallback from "../../components/common/LoadingFallback";
import ErrorFallback from "../../components/common/ErrorFallback";


function IconButton({ children, onClick }) {
  return (
    <button onClick={onClick} className="p-1 hover:bg-gray-100 rounded transition">
      {children}
    </button>
  );
}
function FilterModal({
  isOpen,
  onClose,
  categories,
  setCategories,
  subcategories,
  setSubcategories,
  status,
  setStatus,
  onApply,
  categoriesData,
  subcategoriesData
}) {
  if (!isOpen) return null;

  const statusOptions = ["DRAFT", "PUBLISHED"];

  const handleReset = () => {
    setCategories([]);
    setSubcategories([]);
    setStatus("");
  };

  // Get subcategories for selected categories
  const availableSubcategories = subcategoriesData.filter(sub =>
    categories.length === 0 || categories.includes(sub.categoryId)
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white w-[500px] max-w-full rounded-md p-6 relative max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
        >
          <IconX size={20} />
        </button>
        <h2 className="text-lg font-semibold text-[#0B0C52] mb-4">Filter Options</h2>

        {/* Status Filter */}
        <div className="mb-6">
          <label className="block text-sm font-semibold mb-2">Status</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="border border-gray-300 rounded px-3 py-2 w-full"
          >
            <option value="">Select Status</option>
            {statusOptions.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>

        {/* Categories Filter */}
        <div className="mb-6">
          <label className="block text-sm font-semibold mb-2">Categories</label>
          <div className="space-y-2">
            {categoriesData.map(category => (
              <div key={category.id} className="flex items-center">
                <input
                  type="checkbox"
                  id={`category-${category.id}`}
                  checked={categories.includes(category.id)}
                  onChange={() => {
                    const newCategories = categories.includes(category.id)
                      ? categories.filter(c => c !== category.id)
                      : [...categories, category.id];
                    setCategories(newCategories);
                  }}
                  className="mr-2 h-4 w-4 text-[#0B0C52] focus:ring-[#0B0C52] border-gray-300 rounded"
                />
                <label htmlFor={`category-${category.id}`} className="text-gray-700">
                  {category.name}
                </label>
              </div>
            ))}
          </div>
        </div>

        {/* Subcategories Filter */}
        {categories.length > 0 && (
          <div className="mb-6">
            <label className="block text-sm font-semibold mb-2">Subcategories</label>
            <div className="space-y-2">
              {availableSubcategories.map(subcategory => (
                <div key={subcategory.id} className="flex items-center">
                  <input
                    type="checkbox"
                    id={`subcategory-${subcategory.id}`}
                    checked={subcategories.includes(subcategory.id)}
                    onChange={() => {
                      const newSubcategories = subcategories.includes(subcategory.id)
                        ? subcategories.filter(s => s !== subcategory.id)
                        : [...subcategories, subcategory.id];
                      setSubcategories(newSubcategories);
                    }}
                    className="mr-2 h-4 w-4 text-[#0B0C52] focus:ring-[#0B0C52] border-gray-300 rounded"
                  />
                  <label htmlFor={`subcategory-${subcategory.id}`} className="text-gray-700">
                    {subcategory.name}
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-end gap-4 pt-4 border-t border-gray-200">
          <button
            onClick={handleReset}
            className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:opacity-80"
          >
            Reset
          </button>
          <button
            onClick={onClose}
            className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:opacity-80"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              onApply();
              onClose();
            }}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );
}

/* ============================================
   MODALS
============================================ */
function AddTemplateModal({ onClose }) { /* ... */ }
function EditTemplateModal({ template, onClose }) { /* ... */ }

function DeleteTemplateModal({ template, onClose, onConfirm, isLoading, error }) {
    // This component is already correct from our previous conversation.
    const isBulkDelete = Array.isArray(template);
    let title;
    let message;

    if (isBulkDelete) {
        const count = template.length;
        title = `Delete ${count} Template(s)`;
        message = `Are you sure you want to delete these ${count} selected templates? This action cannot be undone.`;
    } else {
        title = "Delete Template";
        message = `Are you sure you want to delete "${template?.template_name}"? This action cannot be undone.`;
    }

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-md p-6 w-full max-w-md">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold text-red-600">{title}</h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700" disabled={isLoading}>
                        <IconX size={24} />
                    </button>
                </div>
                <p className="text-gray-600">{message}</p>

                {error && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-800 text-sm">
                        <strong>Could not delete:</strong> {error}
                    </div>
                )}

                <div className="flex justify-end gap-4 mt-6">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                        disabled={isLoading}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 disabled:bg-red-300"
                        disabled={isLoading}
                    >
                        {isLoading ? "Deleting..." : "Confirm Delete"}
                    </button>
                </div>
            </div>
        </div>
    );
}


/* --------------------------------------------
   Main Component: DesignTemplate
--------------------------------------------- */
function DesignTemplate() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setLayoutConfig } = useAdminLayout();

  const {
    loading,
    error,
    data: allTemplates,
    filteredData,
    categories: categoriesData,
    subcategories: subcategoriesData,
    categoriesLoading,
    categoriesError,
    currentFilters,
    deleteLoading,
    deleteError,
        searchLoading,
    searchError,
    
  } = useSelector((state) => state.templates);

  // State for modals and pagination
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  // FIX: This state is now split into two to avoid type conflicts
  const [selectedTemplateIDs, setSelectedTemplateIDs] = useState([]);
  const [itemForModal, setItemForModal] = useState(null); 
  const [modalType, setModalType] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  useEffect(() => {
    setLayoutConfig({
      title: "Design Templates",
      showBackButton: false,
      scrollable: false,
    });
    dispatch(fetchTemplates());
    dispatch(fetchCategoriesAndSubcategories());
  }, [dispatch, setLayoutConfig]);

  useEffect(() => {
    const { searchTerm, category, subCategory, status } = currentFilters;
    const hasFilters = searchTerm || category?.length > 0 || subCategory?.length > 0 || status;

    if (hasFilters) {
      dispatch(searchAndFilterFromAllTemplate({
        searchTerm,
        category: category?.join(','),
        subCategory: subCategory?.join(','),
        status
      }));
    } else if (filteredData.length > 0) {
      dispatch(resetFilters());
    }
  }, [currentFilters, dispatch, filteredData.length]);

  let displayData = (currentFilters?.searchTerm || currentFilters?.category?.length > 0 || currentFilters?.subCategory?.length > 0 || currentFilters?.status) 
    ? filteredData 
    : allTemplates;

  if (!displayData) {
    displayData = [];
  }

  const totalCount = displayData.length;
  const totalPages = Math.ceil(totalCount / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentTemplates = displayData.slice(startIndex, endIndex);
  const fromCount = startIndex + 1;
  const toCount = endIndex > totalCount ? totalCount : endIndex;

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((prev) => prev + 1);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage((prev) => prev - 1);
  };

  const handleSearch = (searchTerm) => {
    setCurrentPage(1);
    dispatch(updateFilters({ ...currentFilters, searchTerm }));
  };

  const handleFilter = (categories, subcategories, status) => {
    setCurrentPage(1);
    dispatch(updateFilters({ category: categories, subCategory: subcategories, status }));
  };

  const handleResetFilters = () => {
    setCurrentPage(1);
    dispatch(resetFilters());
  };

  // FIX: Updated modal functions to use the new state `itemForModal`
  function openModal(type, item = null) {
    setItemForModal(item);
    setModalType(type);
  }

  function closeModal() {
    setItemForModal(null);
    setModalType(null);
    if (deleteError) {
        dispatch(clearDeleteError());
    }
  }

  const handleConfirmDelete = async () => {
    // The data for deletion now comes from `itemForModal`
    const idsToDelete = Array.isArray(itemForModal)
      ? itemForModal
      : [itemForModal.id];

    if (!idsToDelete || idsToDelete.length === 0) return;

    try {
      await dispatch(deleteTemplates(idsToDelete)).unwrap();
      closeModal();
      setSelectedTemplateIDs([]); // Clear the checkbox selection
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  if (loading || categoriesLoading) {
    return <LoadingFallback />
  }
  if (error || categoriesError) {
    return <ErrorFallback onRetry={() => {
      dispatch(fetchTemplates());
      dispatch(fetchCategoriesAndSubcategories());
    }} />
  }

  return (
    <div>
      <main className="flex-1 bg-[#FFFFFF] text-gray-800 p-4 rounded">
        <div className="rounded-md bg-[#FFFFFF] p-4">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {/* FIX: Checkbox logic now uses `selectedTemplateIDs` */}
            {selectedTemplateIDs.length > 0 ? (
              <div className="flex flex-wrap items-center justify-between gap-2 mb-4 w-full">
                <div className="flex items-center gap-4">
                  <span className="text-sm text-gray-700 whitespace-nowrap">
                    {selectedTemplateIDs.length} selected
                  </span>
                  <div className="relative ml-1">
                    <input
                      type="text"
                      placeholder="Search Templates by name"
                      value={currentFilters?.searchTerm || ''}
                      onChange={(e) => handleSearch(e.target.value)}
                      className="border-2 border-[#463BC9] rounded text-sm px-2 py-1 pl-8 focus:outline-none focus:border-[3px] peer"
                    />
                    <IconSearch size={16} className="absolute left-2 top-2 text-gray-400 peer-focus:text-[#463BC9]" />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    className="text-red-500 hover:text-red-700 flex items-center gap-1 text-sm px-3 py-1 rounded hover:bg-red-50"
                    onClick={() => openModal("DELETE", selectedTemplateIDs)}
                  >
                    <IconTrash size={16} />
                    Delete
                  </button>
                  <button
                    className="text-gray-500 hover:text-gray-700 text-sm px-3 py-1 rounded hover:bg-gray-100"
                    onClick={() => setSelectedTemplateIDs([])}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
             <>
                
                <div className="relative ml-1">
                  <input
                    type="text"
                    placeholder="Search Templates by name"
                    value={currentFilters?.searchTerm || ''}
                    onChange={(e) => {
                      handleSearch(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="  border-2 border-[#463BC9] rounded text-sm px-2 py-1 pl-8 
                                      focus:outline-none focus:border-[3px] 
                                      peer
                                    "
                  />
                  <IconSearch
                    size={16}
                    className="absolute left-2 top-2 text-gray-400 peer-focus:text-[#463BC9]"
                  />
                </div>
                <span className="text-sm text-gray-700 ml-auto mr-1">
                  {fromCount} - {toCount} of {totalCount}
                </span>
                <button onClick={handlePrevPage} className="p-1 hover:bg-gray-100 rounded">
                  <IconChevronUp size={18} />
                </button>
                <button onClick={handleNextPage} className="p-1 hover:bg-gray-100 rounded">
                  <IconChevronDown size={18} />
                </button>
                <IconButton onClick={() => setIsFilterModalOpen(true)}>
                  <IconFilter size={18} />
                </IconButton>
                {(currentFilters?.category?.length > 0 ||
                  currentFilters?.subCategory?.length > 0 ||
                  currentFilters?.status) && (
                    <IconButton onClick={handleResetFilters}>
                      <IconX size={18} />
                    </IconButton>
                  )}
                <IconButton>
                  <IconRefresh size={18} onClick={() => dispatch(fetchTemplates())} />
                </IconButton>
                <button
                  className="bg-green-500 text-white text-sm px-3 py-1 rounded hover:bg-green-600 flex items-center gap-1"
                  onClick={() => navigate("/admin/templates/new")}
                >
                  <IconCirclePlus size={18} />
                  Add Template
                </button>
       
             </>
            )}
          </div>

          <div className="overflow-x-auto h-[calc(100vh-280px)]">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-[#F9F9FF] z-20 shadow-md">
                <tr className="bg-[#F9F9FF] text-[#120E43]">
                  <th className="px-4 py-2 w-10">
                    <input 
                      type="checkbox"
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedTemplateIDs(currentTemplates.map(t => t.id));
                        } else {
                          setSelectedTemplateIDs([]);
                        }
                      }}
                      checked={selectedTemplateIDs.length === currentTemplates.length && currentTemplates.length > 0}
                    />
                  </th>
                  <th className="px-4 py-2 text-left">Template Name</th>
                  <th className="px-4 py-2 text-left">Categories</th>
                  <th className="px-4 py-2 text-left">Subcategories</th>
                  <th className="px-4 py-2 text-left">Preview</th>
                  <th className="px-4 py-2 text-left">Status</th>
                  <th className="px-4 py-2 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentTemplates.map((tpl) => (
                  <tr key={tpl.id} className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="px-4 py-2">
                      {/* FIX: Checkbox logic now uses `selectedTemplateIDs` */}
                      <input
                        type="checkbox"
                        checked={selectedTemplateIDs.includes(tpl.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedTemplateIDs([...selectedTemplateIDs, tpl.id]);
                          } else {
                            setSelectedTemplateIDs(selectedTemplateIDs.filter(id => id !== tpl.id));
                          }
                        }}
                      />
                    </td>
                    <td className="px-4 py-2">{tpl.template_name}</td>
                    <td className="px-4 py-2">{tpl.category_name}</td>
                    <td className="px-4 py-2">{tpl.sub_category_name}</td>
                    <td className="px-4 py-2">
                      <div className="relative w-16 h-16">
                        {tpl.media_files?.[0]?.front_image && (
                          <img src={tpl.media_files[0].front_image} alt="Front preview" className="absolute w-12 h-12 object-cover rounded border border-gray-200 shadow-sm transform rotate-2 z-10" />
                        )}
                        {tpl.media_files?.[0]?.back_image && (
                          <img src={tpl.media_files[0].back_image} alt="Back preview" className="absolute w-12 h-12 object-cover rounded border border-gray-200 shadow-sm transform -rotate-2 left-2 top-2" />
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-1 rounded-full text-xs ${tpl.status === 'PUBLISHED' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                        {tpl.status}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-2">
                        <button
                          className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                          onClick={() => navigate('/admin/templates/' + tpl.id)}
                        >
                          <IconPencil size={16} />
                          Edit
                        </button>
                        <button
                          className="text-red-500 hover:text-red-700 flex items-center gap-1"
                          onClick={() => openModal("DELETE", tpl)}
                        >
                          <IconTrash size={16} />
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {currentTemplates.length === 0 && (
                  <tr>
                    <td colSpan="7" className="text-center py-4 text-gray-500">
                      No templates found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-2 flex flex-col sm:flex-row items-start sm:items-center justify-between text-sm text-gray-600">
            <div />
            <div className="mt-2 sm:mt-0 space-x-4">
              <span className="text-[#0B0C52]">Total Templates: <strong>{allTemplates.length}</strong></span>
              <span className="text-[#0B0C52]">Published: <strong>{allTemplates.filter(t => t.status === 'PUBLISHED').length}</strong></span>
              <span className="text-[#0B0C52]">Draft: <strong>{allTemplates.filter(t => t.status === 'DRAFT').length}</strong></span>
            </div>
          </div>
        </div>
      </main>

      {/* FIX: Modal invocation is now based on `itemForModal` */}
      {modalType === "ADD" && <AddTemplateModal onClose={closeModal} />}
      {modalType === "EDIT" && itemForModal && <EditTemplateModal template={itemForModal} onClose={closeModal} />}
      
      {modalType === "DELETE" && itemForModal && (
        <DeleteTemplateModal
          template={itemForModal}
          onClose={closeModal}
          onConfirm={handleConfirmDelete}
          isLoading={deleteLoading}
          error={deleteError}
        />
      )}

      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        categories={currentFilters?.category || []}
        setCategories={(categories) => dispatch(updateFilters({ category: categories }))}
        subcategories={currentFilters?.subCategory || []}
        setSubcategories={(subcategories) => dispatch(updateFilters({ subCategory: subcategories }))}
        status={currentFilters?.status || ''}
        setStatus={(status) => dispatch(updateFilters({ status }))}
        onApply={() => handleFilter(
          currentFilters?.category || [],
          currentFilters?.subCategory || [],
          currentFilters?.status || ''
        )}
        categoriesData={categoriesData}
        subcategoriesData={subcategoriesData}
      />
    </div>
  );
}

export default DesignTemplate;